import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { weekLabel } from "@/lib/format";
import { EmptyState, ErrorState, Loading, Page, PageHeader } from "@/components/kit";
import { SalesImport, ComparisonKpis, UnmatchedSales, SalesTable, DeviationTable, downloadTemplate } from "@/components/analysis/AnalysisParts";
import WeeklyChart from "@/components/analysis/WeeklyChart";

function useAnalysis(cid) {
  const qc = useQueryClient();
  const key = ["theoretical", cid];
  const query = useQuery({ queryKey: key, queryFn: () => api(`/conferences/${cid}/theoretical`), enabled: !!cid });
  const opts = (msg) => ({ onSuccess: (d) => { qc.setQueryData(key, d); qc.invalidateQueries({ queryKey: ["theoretical-weekly"] }); if (msg) toast.success(typeof msg === "function" ? msg(d) : msg); }, onError: (e) => toast.error(e.message) });
  const imp = useMutation({ mutationFn: (file) => { const fd = new FormData(); fd.append("file", file); return api(`/conferences/${cid}/sales`, { method: "POST", form: fd }); }, ...opts((d) => `${d.sales.length + d.unmatched.length} itens importados`) });
  const map = useMutation({ mutationFn: ({ name, link }) => api(`/conferences/${cid}/sales/map`, { method: "PUT", body: { name, ...link } }), ...opts("Item vinculado") });
  const del = useMutation({ mutationFn: () => api(`/conferences/${cid}/sales`, { method: "DELETE" }), ...opts("Vendas removidas") });
  return { ...query, imp, map, del };
}

function ConferenceSelect({ confs, value, onChange }) {
  return (
    <select className="input mb-5 max-w-sm" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid="analysis-conference-select">
      {confs.map((c) => <option key={c.id} value={c.id}>{weekLabel(c)} · {c.status === "aberta" ? "aberta" : "finalizada"}</option>)}
    </select>
  );
}

export default function CmvAnalysis() {
  const [params, setParams] = useSearchParams();
  const { data: confs = [], isLoading } = useQuery({ queryKey: ["conferences"], queryFn: () => api("/conferences") });
  const { data: recipes = [] } = useQuery({ queryKey: ["recipes"], queryFn: () => api("/recipes") });
  const { data: products = [] } = useQuery({ queryKey: ["products"], queryFn: () => api("/products") });
  const [cid, setCid] = useState(params.get("conf"));
  useEffect(() => {
    if (cid || !confs.length) return;
    setCid((confs.find((c) => c.status === "finalizada") || confs[0]).id);
  }, [confs, cid]);
  const a = useAnalysis(cid);
  const pick = (id) => { setCid(id); setParams({ conf: id }, { replace: true }); };
  const header = <PageHeader title="CMV teórico x real" subtitle="Compare o custo previsto pelas fichas com o consumo real da conferência" back="/recipes" />;
  if (isLoading) return <Loading />;
  if (!confs.length) return <Page>{header}<EmptyState title="Nenhuma conferência" text="Crie uma conferência de estoque para comparar com as vendas." /></Page>;
  const d = a.data;
  return (
    <Page>
      {header}
      <ConferenceSelect confs={confs} value={cid} onChange={pick} />
      <div className="mb-5"><WeeklyChart onPick={pick} /></div>
      {a.isLoading || !d ? (a.isError ? <ErrorState message={a.error.message} onRetry={a.refetch} /> : <Loading />) : (
        <div className="flex flex-col gap-5" data-testid="cmv-analysis">
          <SalesImport data={d} busy={a.imp.isPending} onFile={(file) => a.imp.mutate(file)} onRemove={() => window.confirm("Remover as vendas importadas deste período?") && a.del.mutate()} onTemplate={() => downloadTemplate().catch((e) => toast.error(e.message))} />
          {d.has_sales && (
            <>
              <ComparisonKpis t={d.totals} target={d.target} />
              <UnmatchedSales rows={d.unmatched} recipes={recipes} products={products} busy={a.map.isPending} onMap={(name, link) => a.map.mutate({ name, link })} />
              <DeviationTable rows={d.products} productLevel={d.product_level} />
              <SalesTable rows={d.sales} />
            </>
          )}
        </div>
      )}
    </Page>
  );
}
